import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import {
  Save,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Star,
  ArrowLeft,
} from "lucide-react";
import { toast } from "react-toastify";
import { Editor } from "@tinymce/tinymce-react";
import { server, tinyMceKey } from "../server";
import { useNavigate, useLocation } from "react-router-dom";

const EditNote = () => {
  const location = useLocation();
  const { notesId } = location?.state || {};
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [noteData, setNoteData] = useState(null);
  const editorRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      title: "",
      isPublished: false,
      isFeatured: false,
    },
  });

  // Fetch note data
  const fetchNote = async () => {
    try {
      setIsLoading(true);
      const { data } = await axios.get(`${server}/notes/getById`, {
        params: { notesId },
      });

      if (data.success && data.data) {
        const note = data.data;
        setNoteData(note);

        console.log("Note content:", note.content); // Debug log

        // Set form values from API response
        setValue("title", note.title || "");
        setValue("isPublished", note.isPublished || false);
        setValue("isFeatured", note.isFeatured || false);

        // Fetch subjects first
        await fetchSubjects();

        // Try to find subject and chapter IDs from names
        if (note.subject && note.chapter) {
          await findSubjectAndChapterIds(note.subject, note.chapter);
        }
      } else {
        toast.error("Note not found");
        navigate(-1);
      }
    } catch (error) {
      toast.error("Failed to load note");
      console.error("Error fetching note:", error);
      navigate(-1);
    } finally {
      setIsLoading(false);
    }
  };

  // Find subject and chapter IDs from names
  const findSubjectAndChapterIds = async (subjectName, chapterName) => {
    try {
      // Find subject ID by name
      const subject = subjects.find((sub) => sub.subject === subjectName);
      if (subject) {
        console.log("Found subject:", subject); // Debug log
        setSelectedSubject(subject.id.toString());

        // Fetch chapters for this subject
        await fetchChapters(subject.id);

        // Find chapter ID by name
        const chapter = chapters.find((chap) => chap.chapter === chapterName);
        if (chapter) {
          console.log("Found chapter:", chapter); // Debug log
          setSelectedChapter(chapter.id.toString());
        }
      } else {
        console.log("Subject not found:", subjectName); // Debug log
      }
    } catch (error) {
      console.error("Error finding subject/chapter:", error);
    }
  };

  // Fetch subjects
  const fetchSubjects = async () => {
    try {
      const { data } = await axios.get(`${server}/subject/all`, {
        params: { pageNo: 1, pageSize: 100 },
      });
      setSubjects(data?.data || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  // Fetch chapters
  const fetchChapters = async (subjectId) => {
    try {
      const { data } = await axios.get(`${server}/chapter/all`, {
        params: { subjectId },
      });
      setChapters(data?.data || []);
    } catch (error) {
      console.error("Error fetching chapters:", error);
    }
  };

  useEffect(() => {
    if (notesId) {
      fetchNote();
    }
  }, [notesId]);

  useEffect(() => {
    if (selectedSubject) {
      fetchChapters(selectedSubject);
    } else {
      setChapters([]);
      setSelectedChapter("");
    }
  }, [selectedSubject]);

  // Effect to set editor content when noteData is available
  useEffect(() => {
    if (noteData && noteData.content && editorRef.current) {
      console.log("Setting editor content:", noteData.content); // Debug log
      editorRef.current.setContent(noteData.content);
    }
  }, [noteData]);

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);

      if (!selectedSubject) {
        toast.error("Please select a subject");
        return;
      }

      if (!selectedChapter) {
        toast.error("Please select a chapter");
        return;
      }

      const content = editorRef.current ? editorRef.current.getContent() : "";

      if (!content.trim()) {
        toast.error("Please add note content");
        return;
      }

      const formData = new FormData();
      formData.append("title", data.title);
      formData.append("content", content);
      formData.append("subjectId", selectedSubject);
      formData.append("chapterId", selectedChapter);
      formData.append("isPublished", data.isPublished);
      formData.append("isFeatured", data.isFeatured);

      const { data: resData } = await axios.put(
        `${server}/notes/${notesId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success(resData?.message || "Note updated successfully!");
      navigate(-1);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update note");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    fetchNote();
  };

  // Handle subject selection
  const handleSubjectChange = (subjectId) => {
    setSelectedSubject(subjectId);
  };

  // Handle chapter selection
  const handleChapterChange = (chapterId) => {
    setSelectedChapter(chapterId);
  };

  const getSelectedSubjectName = () => {
    const subject = subjects.find(
      (sub) => sub.id === parseInt(selectedSubject)
    );
    return subject ? subject.subject : "";
  };

  const getSelectedChapterName = () => {
    const chapter = chapters.find(
      (chap) => chap.id === parseInt(selectedChapter)
    );
    return chapter ? chapter.chapter : "";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-300">Loading note...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white">Edit Note</h1>
            <p className="text-gray-400">Update your educational content</p>
          </div>
          <div className="w-20"></div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Subject & Chapter Selection */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <label className="block text-sm font-semibold text-white mb-3">
              Select Subject & Chapter *
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Subject Selection */}
              <div>
                <label className="block text-xs text-gray-400 mb-2">
                  Subject
                </label>
                <select
                  className="w-full px-4 py-2.5 border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 text-white bg-gray-700"
                  value={selectedSubject}
                  onChange={(e) => handleSubjectChange(e.target.value)}
                >
                  <option value="">Choose a subject</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.subject}
                    </option>
                  ))}
                </select>
              </div>

              {/* Chapter Selection */}
              <div>
                <label className="block text-xs text-gray-400 mb-2">
                  Chapter
                </label>
                <select
                  className="w-full px-4 py-2.5 border border-gray-600 rounded-lg focus:outline-none focus:border-green-500 text-white bg-gray-700"
                  value={selectedChapter}
                  onChange={(e) => handleChapterChange(e.target.value)}
                  disabled={!selectedSubject}
                >
                  <option value="">Choose a chapter</option>
                  {chapters.map((chapter) => (
                    <option key={chapter.id} value={chapter.id}>
                      {chapter.chapter}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {(selectedSubject || selectedChapter) && (
              <div className="mt-4 p-3 bg-blue-500/20 rounded-lg border border-blue-500/30">
                <p className="text-sm text-blue-300">
                  Selected:{" "}
                  <span className="font-semibold">
                    {getSelectedSubjectName()}
                    {selectedChapter && ` → ${getSelectedChapterName()}`}
                  </span>
                </p>
              </div>
            )}
          </div>

          {/* Note Title */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <label className="block text-sm font-semibold text-white mb-3">
              Note Title *
            </label>
            <input
              type="text"
              className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none text-white bg-gray-700 ${
                errors.title
                  ? "border-red-500 focus:border-red-500"
                  : "border-gray-600 focus:border-green-500"
              }`}
              placeholder="Enter note title..."
              {...register("title", {
                required: "Note title is required",
                minLength: {
                  value: 3,
                  message: "Title must be at least 3 characters",
                },
              })}
            />
            {errors.title && (
              <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.title.message}
              </p>
            )}
          </div>

          {/* Note Content */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <label className="block text-sm font-semibold text-white mb-3">
              Note Content *
            </label>
            <div className="border border-gray-600 rounded-lg overflow-hidden">
              <Editor
                apiKey={tinyMceKey}
                onInit={(evt, editor) => {
                  editorRef.current = editor;
                  // Set content when editor is ready
                  if (noteData?.content) {
                    setTimeout(() => {
                      editor.setContent(noteData.content);
                    }, 100);
                  }
                }}
                initialValue={noteData?.content || ""}
                init={{
                  height: 400,
                  menubar: true,
                  plugins: [
                    "advlist",
                    "autolink",
                    "lists",
                    "link",
                    "image",
                    "charmap",
                    "preview",
                    "anchor",
                    "searchreplace",
                    "visualblocks",
                    "code",
                    "fullscreen",
                    "insertdatetime",
                    "media",
                    "table",
                    "code",
                    "help",
                    "wordcount",
                  ],
                  toolbar:
                    "undo redo | blocks | bold italic underline | " +
                    "alignleft aligncenter alignright alignjustify | " +
                    "bullist numlist outdent indent | link image media | " +
                    "forecolor backcolor | code | fullscreen | preview | help",
                  skin: "oxide-dark",
                  content_css: "dark",
                  content_style: `
                    body { 
                      background: #1f2937; 
                      color: #f9fafb; 
                      font-family: Inter, sans-serif; 
                      font-size: 14px; 
                      line-height: 1.6;
                    }
                    p { margin: 0 0 1em 0; }
                    h1, h2, h3, h4, h5, h6 { color: #f9fafb; }
                  `,
                  setup: (editor) => {
                    editor.on("init", () => {
                      if (noteData?.content) {
                        editor.setContent(noteData.content);
                      }
                    });
                  },
                }}
              />
            </div>
          </div>

          {/* Publishing Options */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Publish Toggle */}
              <label className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/20 rounded-lg border border-green-500/30">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <span className="block text-sm font-semibold text-white">
                      Publish
                    </span>
                    <span className="block text-xs text-gray-400">
                      Make visible to readers
                    </span>
                  </div>
                </div>
                <input
                  type="checkbox"
                  className="hidden"
                  {...register("isPublished")}
                />
                <div
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    watch("isPublished") ? "bg-green-500" : "bg-gray-600"
                  }`}
                >
                  <div
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-lg transition-transform ${
                      watch("isPublished") ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </div>
              </label>

              {/* Featured Toggle */}
              <label className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
                    <Star className="w-4 h-4 text-yellow-400" />
                  </div>
                  <div>
                    <span className="block text-sm font-semibold text-white">
                      Featured
                    </span>
                    <span className="block text-xs text-gray-400">
                      Highlight this note
                    </span>
                  </div>
                </div>
                <input
                  type="checkbox"
                  className="hidden"
                  {...register("isFeatured")}
                />
                <div
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    watch("isFeatured") ? "bg-yellow-500" : "bg-gray-600"
                  }`}
                >
                  <div
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-lg transition-transform ${
                      watch("isFeatured") ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </div>
              </label>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Update Note
                </>
              )}
            </button>

            <button
              type="button"
              onClick={resetForm}
              className="flex items-center justify-center gap-2 px-6 py-3 border border-gray-600 text-gray-300 font-semibold rounded-lg hover:border-gray-500 hover:bg-gray-700"
            >
              <RefreshCw className="w-4 h-4" />
              Reset
            </button>

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center justify-center gap-2 px-6 py-3 border border-gray-600 text-gray-300 font-semibold rounded-lg hover:border-gray-500 hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditNote;
